import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './KYBManagement.css';
import { getKYBVerifications, approveKYB, rejectKYB, getKYBById } from '../../../api/services/adminService';
import { fetchAuthenticatedImage } from '../../../api/services/authService';

const KYBManagement = () => {
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [kybRequests, setKybRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isLastPage, setIsLastPage] = useState(true);
  const [isFirstPage, setIsFirstPage] = useState(true);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successModalMessage, setSuccessModalMessage] = useState('');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingKybId, setRejectingKybId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionReasonCode, setRejectionReasonCode] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' }); // type: 'success' or 'error'
  const [imageUrls, setImageUrls] = useState({
    document1: null,
    document2: null,
    document3: null
  });
  const [imageLoading, setImageLoading] = useState({
    document1: false,
    document2: false,
    document3: false
  });
  
  // Map API response to component format
  const mapApiResponseToComponent = useCallback((apiData) => {
    if (!apiData || !apiData.content) return [];
    
    return apiData.content.map(item => {
      // Map status from API format (PENDING_REVIEW, VERIFIED/APPROVED, REJECTED) to lowercase
      const statusMap = {
        'PENDING_REVIEW': 'pending',
        'VERIFIED': 'verified',
        'APPROVED': 'verified',  // Backend may return APPROVED instead of VERIFIED
        'REJECTED': 'rejected',
        'REJECTED_RESUBMIT_ALLOWED': 'rejected'
      };
      
      // Format date from ISO string to readable format
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
      };
      
      return {
        id: item.id,
        userId: item.userId,
        name: item.userName || item.userEmail || 'Unknown',
        email: item.userEmail || item.email,
        phone: 'N/A', // Not provided in API response
        address: 'N/A', // Not provided in API response
        role: 'Landlord', // Default, may need to fetch from user profile
        submittedDate: formatDate(item.createdAt),
        status: statusMap[item.status] || item.status.toLowerCase(),
        idType: 'N/A', // Not provided in KYB response
        idNumber: 'N/A', // Not provided in KYB response
        dateOfBirth: 'N/A', // Not provided in API response
        propertyOwnershipDocument: item.documentUrl1 || null,
        kraPinCertificate: item.documentUrl2 || null,
        businessRegistrationDocument: item.documentUrl3 || null,
        rejectionReason: item.rejectionReason || null,
        rejectionReasonCode: item.rejectionReasonCode || null,
        resubmitDeadline: item.resubmitDeadline ? formatDate(item.resubmitDeadline) : null,
        verifiedAt: item.verifiedAt ? formatDate(item.verifiedAt) : null,
        createdAt: item.createdAt
      };
    });
  }, []);

  // Fetch KYB verifications from API
  const fetchKYBVerifications = useCallback(async (status = 'pending', pageNum = 0) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getKYBVerifications(status, pageNum, 20);
      
      if (response.status === 'success' && response.data) {
        const mappedData = mapApiResponseToComponent(response.data);
        setKybRequests(mappedData);
        
        // Handle pagination - Spring Boot pagination response structure
        setIsLastPage(response.data.last !== undefined ? response.data.last : true);
        setIsFirstPage(response.data.first !== undefined ? response.data.first : true);
        
        // Handle totalPages
        if (response.data.totalPages !== undefined) {
          setTotalPages(response.data.totalPages);
        } else if (response.data.last !== undefined) {
          if (response.data.last) {
            setTotalPages(response.data.number + 1);
          } else {
            setTotalPages(response.data.number + 2);
          }
        }
        
        // Handle totalElements
        if (response.data.totalElements !== undefined) {
          setTotalElements(response.data.totalElements);
        } else if (response.data.last && response.data.numberOfElements !== undefined) {
          const calculatedTotal = (response.data.number * response.data.size) + response.data.numberOfElements;
          setTotalElements(calculatedTotal);
        }
      }
    } catch (err) {
      console.error('Error fetching KYB verifications:', err);
      setError(err.message || 'Failed to load KYB verifications');
    } finally {
      setIsLoading(false);
    }
  }, [mapApiResponseToComponent]);

  // Fetch data when component mounts or filters change
  useEffect(() => {
    const statusToFetch = filter === 'all' ? 'pending' : filter;
    fetchKYBVerifications(statusToFetch, page);
  }, [filter, page, fetchKYBVerifications]);

  const filteredRequests = kybRequests.filter(req => {
    // Filter by status - ensure we only show items matching the selected filter
    const statusMatch = filter === 'all' || req.status === filter;
    return statusMatch;
  });

  // Fetch KYB details by ID
  const fetchKYBById = useCallback(async (kybId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getKYBById(kybId);
      
      if (response.status === 'success' && response.data) {
        // Map the single item using the same mapping function
        const mappedData = mapApiResponseToComponent({ content: [response.data] });
        if (mappedData.length > 0) {
          setSelectedRequest(mappedData[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching KYB details:', err);
      setError(err.message || 'Failed to load KYB details');
      // If fetch fails, try to use the request from the list
      const existingRequest = kybRequests.find(req => req.id === kybId);
      if (existingRequest) {
        setSelectedRequest(existingRequest);
      }
    } finally {
      setIsLoading(false);
    }
  }, [mapApiResponseToComponent, kybRequests]);

  const handleVerify = async (id) => {
    try {
      setIsLoading(true);
      const response = await approveKYB(id);
      // Use the message from API response, or fallback to default
      const successMessage = response.message || 'KYB verification approved successfully!';
      setSuccessModalMessage(successMessage);
      // Refresh the list
      const statusToFetch = filter === 'all' ? 'pending' : filter;
      await fetchKYBVerifications(statusToFetch, page);
      setSelectedRequest(null);
      // Show success modal
      setTimeout(() => {
        setSuccessModalOpen(true);
      }, 100);
    } catch (err) {
      console.error('Error approving KYB:', err);
      const errorResponse = err.response || {};
      const errorData = errorResponse.data || errorResponse;
      const errorMessage = (err.message || '').toLowerCase();
      const apiErrorMessage = (errorData?.message || errorData?.error || '').toLowerCase();
      
      // Check if error is related to KYC approval requirement
      if (errorMessage.includes('kyc') || apiErrorMessage.includes('kyc') || errorMessage.includes('approve this user')) {
        setErrorModalMessage('KYC must be approved before approving KYB.');
      } else {
        const displayMessage = err.message || errorData?.message || errorData?.error || 'Failed to approve KYB. Please try again.';
        setErrorModalMessage(displayMessage);
      }
      setTimeout(() => {
        setErrorModalOpen(true);
      }, 100);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = (id) => {
    setRejectingKybId(id);
    setRejectionReason('');
    setRejectionReasonCode('');
    setRejectModalOpen(true);
  };

  const handleCloseRejectModal = () => {
    setRejectModalOpen(false);
    setRejectingKybId(null);
    setRejectionReason('');
    setRejectionReasonCode('');
  };

  const handleSubmitRejection = async () => {
    if (!rejectionReason || rejectionReason.trim() === '') {
      setErrorModalMessage('Please provide a rejection reason');
      setTimeout(() => {
        setErrorModalOpen(true);
      }, 100);
      return;
    }
    
    if (!rejectionReasonCode || rejectionReasonCode.trim() === '') {
      setErrorModalMessage('Please provide a rejection reason code');
      setTimeout(() => {
        setErrorModalOpen(true);
      }, 100);
      return;
    }
    
    try {
      setIsLoading(true);
      await rejectKYB(rejectingKybId, rejectionReason.trim(), rejectionReasonCode.trim());
      setSuccessModalMessage('KYB verification rejected successfully!');
      // Refresh the list
      const statusToFetch = filter === 'all' ? 'pending' : filter;
      await fetchKYBVerifications(statusToFetch, page);
      setSelectedRequest(null);
      handleCloseRejectModal();
      // Show success modal
      setTimeout(() => {
        setSuccessModalOpen(true);
      }, 100);
    } catch (err) {
      console.error('Error rejecting KYB:', err);
      const errorResponse = err.response || {};
      const errorData = errorResponse.data || errorResponse;
      const displayMessage = err.message || errorData?.message || errorData?.error || 'Failed to reject KYB. Please try again.';
      setErrorModalMessage(displayMessage);
      setTimeout(() => {
        setErrorModalOpen(true);
      }, 100);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = async (request) => {
    // First set the request from the list (for immediate display)
    setSelectedRequest(request);
    
    // Then fetch fresh data from API
    await fetchKYBById(request.id);
  };

  const handleCloseDetails = () => {
    setSelectedRequest(null);
    // Clean up blob URLs when closing
    setImageUrls(prevUrls => {
      Object.values(prevUrls).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      return { document1: null, document2: null, document3: null };
    });
  };

  // Helper function to check if a URL is a PDF
  const isPDF = (url) => {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    return lowerUrl.endsWith('.pdf') || lowerUrl.includes('.pdf?');
  };

  // Check if images are already loaded after URLs are set
  useEffect(() => {
    const checkImageLoad = () => {
      // Check Document 1
      if (imageUrls.document1 && imageLoading.document1) {
        const img = document.querySelector(`img[alt="Document 1"]`);
        if (img && img.complete) {
          setImageLoading(prev => ({ ...prev, document1: false }));
        }
      }
      // Check Document 2
      if (imageUrls.document2 && imageLoading.document2) {
        const img = document.querySelector(`img[alt="Document 2"]`);
        if (img && img.complete) {
          setImageLoading(prev => ({ ...prev, document2: false }));
        }
      }
      // Check Document 3
      if (imageUrls.document3 && imageLoading.document3) {
        const img = document.querySelector(`img[alt="Document 3"]`);
        if (img && img.complete) {
          setImageLoading(prev => ({ ...prev, document3: false }));
        }
      }
    };

    // Check immediately and after a short delay
    checkImageLoad();
    const timeoutId = setTimeout(checkImageLoad, 100);
    const timeoutId2 = setTimeout(checkImageLoad, 500);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
    };
  }, [imageUrls.document1, imageUrls.document2, imageUrls.document3, imageLoading.document1, imageLoading.document2, imageLoading.document3]);

  // Fetch authenticated images when selectedRequest changes
  useEffect(() => {
    const document1 = selectedRequest?.propertyOwnershipDocument;
    const document2 = selectedRequest?.kraPinCertificate;
    const document3 = selectedRequest?.businessRegistrationDocument;

    const loadAuthenticatedImages = async () => {
      const newImageUrls = {
        document1: null,
        document2: null,
        document3: null
      };

      setImageLoading({ document1: true, document2: true, document3: true });

      // Fetch Document 1 - For both PDFs and images, fetch as blob for iframe/img display
      if (document1 && document1.startsWith('http')) {
        try {
          // Fetch and convert to blob URL (works for both PDFs and images)
          const blobUrl = await fetchAuthenticatedImage(document1);
          if (blobUrl) {
            newImageUrls.document1 = blobUrl;
            // Set URL immediately so it can start loading
            setImageUrls(prev => ({ ...prev, document1: blobUrl }));
            // Set a timeout fallback in case onLoad doesn't fire (e.g., cached images)
            setTimeout(() => {
              setImageLoading(prev => {
                if (prev.document1) {
                  return { ...prev, document1: false };
                }
                return prev;
              });
            }, 1500);
          } else {
            setImageLoading(prev => ({ ...prev, document1: false }));
          }
        } catch (error) {
          console.error('Error loading Document 1:', error);
          setImageLoading(prev => ({ ...prev, document1: false }));
        }
      } else {
        setImageLoading(prev => ({ ...prev, document1: false }));
      }

      // Fetch Document 2
      if (document2 && document2.startsWith('http')) {
        try {
          const blobUrl = await fetchAuthenticatedImage(document2);
          if (blobUrl) {
            newImageUrls.document2 = blobUrl;
            // Set URL immediately so it can start loading
            setImageUrls(prev => ({ ...prev, document2: blobUrl }));
            // Set a timeout fallback in case onLoad doesn't fire (e.g., cached images)
            setTimeout(() => {
              setImageLoading(prev => {
                if (prev.document2) {
                  return { ...prev, document2: false };
                }
                return prev;
              });
            }, 1500);
          } else {
            setImageLoading(prev => ({ ...prev, document2: false }));
          }
        } catch (error) {
          console.error('Error loading Document 2:', error);
          setImageLoading(prev => ({ ...prev, document2: false }));
        }
      } else {
        setImageLoading(prev => ({ ...prev, document2: false }));
      }

      // Fetch Document 3
      if (document3 && document3.startsWith('http')) {
        try {
          const blobUrl = await fetchAuthenticatedImage(document3);
          if (blobUrl) {
            newImageUrls.document3 = blobUrl;
            // Set URL immediately so it can start loading
            setImageUrls(prev => ({ ...prev, document3: blobUrl }));
            // Set a timeout fallback in case onLoad doesn't fire (e.g., cached images)
            setTimeout(() => {
              setImageLoading(prev => {
                if (prev.document3) {
                  return { ...prev, document3: false };
                }
                return prev;
              });
            }, 1500);
          } else {
            setImageLoading(prev => ({ ...prev, document3: false }));
          }
        } catch (error) {
          console.error('Error loading Document 3:', error);
          setImageLoading(prev => ({ ...prev, document3: false }));
        }
      } else {
        setImageLoading(prev => ({ ...prev, document3: false }));
      }

      // Update any remaining URLs
      setImageUrls(prev => ({ ...prev, ...newImageUrls }));
    };

    // Clean up previous blob URLs before loading new ones
    setImageUrls(prevUrls => {
      Object.values(prevUrls).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      return { document1: null, document2: null, document3: null };
    });

    if (document1 || document2 || document3) {
      loadAuthenticatedImages();
    }

    // Cleanup function
    return () => {
      setImageUrls(prevUrls => {
        Object.values(prevUrls).forEach(url => {
          if (url && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });
        return { document1: null, document2: null, document3: null };
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRequest?.propertyOwnershipDocument, selectedRequest?.kraPinCertificate, selectedRequest?.businessRegistrationDocument]);

  // Function to open document in new tab
  const handleOpenInNewTab = (imageUrl, originalUrl) => {
    if (imageUrl && imageUrl.startsWith('blob:')) {
      // For blob URLs, open in new window
      const newWindow = window.open();
      if (newWindow) {
        newWindow.location.href = imageUrl;
      }
    } else if (originalUrl && originalUrl.startsWith('http')) {
      // For PDFs, open directly
      if (isPDF(originalUrl)) {
        const newWindow = window.open();
        if (newWindow) {
          // Fetch authenticated PDF and open
          fetchAuthenticatedImage(originalUrl).then(blobUrl => {
            if (blobUrl) {
              newWindow.location.href = blobUrl;
            } else {
              // Fallback: try opening original URL (browser will handle auth)
              newWindow.location.href = originalUrl;
            }
          }).catch(error => {
            console.error('Error opening PDF in new tab:', error);
            // Fallback: try opening original URL
            const fallbackWindow = window.open();
            if (fallbackWindow) {
              fallbackWindow.location.href = originalUrl;
            }
          });
        }
      } else {
        // For images, fetch with auth and open
        fetchAuthenticatedImage(originalUrl).then(blobUrl => {
          if (blobUrl) {
            const newWindow = window.open();
            if (newWindow) {
              newWindow.location.href = blobUrl;
            }
          }
        }).catch(error => {
          console.error('Error opening image in new tab:', error);
          setErrorModalMessage('Failed to open document in new tab');
          setTimeout(() => {
            setErrorModalOpen(true);
          }, 100);
        });
      }
    }
  };

  return (
    <div className="kyb-management-container">
      <h1 className="page-title">KYB Verification Management</h1>
      
      {/* Success/Error Message Display */}
      {message.text && (
        <div 
          className="message-banner" 
          style={{ 
            padding: '12px 16px', 
            backgroundColor: message.type === 'success' ? '#d1fae5' : '#fee2e2', 
            color: message.type === 'success' ? '#065f46' : '#991b1b', 
            borderRadius: '8px', 
            marginBottom: '16px',
            border: `1px solid ${message.type === 'success' ? '#3EA43B' : '#EF4444'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>{message.text}</span>
          <button
            onClick={() => setMessage({ text: '', type: '' })}
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '0 8px',
              fontWeight: 'bold'
            }}
            aria-label="Close message"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="error-message" style={{ 
          padding: '12px', 
          backgroundColor: '#fee2e2', 
          color: '#991b1b', 
          borderRadius: '8px', 
          marginBottom: '16px',
          border: '1px solid #EF4444'
        }}>
          Error: {error}
        </div>
      )}
      
      <div className="kyb-header">
        <div className="filter-controls">
          <label htmlFor="kyb-status-filter" className="filter-label">Filter by Status:</label>
          <select 
            id="kyb-status-filter"
            className="filter-dropdown"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(0); // Reset to first page when filter changes
            }}
            disabled={isLoading}
          >
            <option value="pending">Pending</option>
            <option value="verified">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {isLoading && kybRequests.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <p>Loading KYB verifications...</p>
        </div>
      )}

      {!isLoading && filteredRequests.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
          <p style={{ color: '#8B8B8B', fontSize: '16px' }}>No KYB verifications found matching the selected filters.</p>
        </div>
      )}

      <div className="kyb-requests">
        {filteredRequests.map((request) => (
          <div key={request.id} className="kyb-card">
            <div className="kyb-card-header">
              <div className="kyb-user-info">
                <h3>{request.name}</h3>
                <p className="kyb-email">{request.email}</p>
              </div>
              <span className={`kyb-status-badge ${request.status}`}>{request.status}</span>
            </div>
            
            <div className="kyb-card-body">
              <div className="kyb-details-grid">
                <div className="kyb-detail-item">
                  <span className="detail-label">Role:</span>
                  <span className="detail-value">{request.role}</span>
                </div>
                {request.phone && request.phone !== 'N/A' && (
                  <div className="kyb-detail-item">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{request.phone}</span>
                  </div>
                )}
                {request.idType && request.idType !== 'N/A' && (
                  <div className="kyb-detail-item">
                    <span className="detail-label">ID Type:</span>
                    <span className="detail-value">{request.idType}</span>
                  </div>
                )}
                {request.idNumber && request.idNumber !== 'N/A' && (
                  <div className="kyb-detail-item">
                    <span className="detail-label">ID Number:</span>
                    <span className="detail-value">{request.idNumber}</span>
                  </div>
                )}
                {request.dateOfBirth && request.dateOfBirth !== 'N/A' && (
                  <div className="kyb-detail-item">
                    <span className="detail-label">Date of Birth:</span>
                    <span className="detail-value">{request.dateOfBirth}</span>
                  </div>
                )}
                <div className="kyb-detail-item">
                  <span className="detail-label">Submitted:</span>
                  <span className="detail-value">{request.submittedDate}</span>
                </div>
              </div>

              <div className="kyb-actions">
                <button 
                  className="btn btn-view" 
                  onClick={() => handleViewDetails(request)}
                  disabled={isLoading}
                >
                  View Documents
                </button>
                {request.status === 'pending' && (
                  <>
                    <button 
                      className="btn btn-verify" 
                      onClick={() => handleVerify(request.id)}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Processing...' : 'Approve'}
                    </button>
                    <button 
                      className="btn btn-reject" 
                      onClick={() => handleReject(request.id)}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Processing...' : 'Reject'}
                    </button>
                  </>
                )}
              </div>

              {request.status === 'rejected' && request.rejectionReason && (
                <div className="rejection-reason">
                  <span className="rejection-label">Rejection Reason:</span>
                  <span className="rejection-text">{request.rejectionReason}</span>
                  {request.rejectionReasonCode && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#8B8B8B' }}>
                      Code: {request.rejectionReasonCode}
                    </div>
                  )}
                </div>
              )}

              {request.status === 'verified' && request.verifiedAt && (
                <div className="approval-info" style={{ marginTop: '12px', padding: '8px', backgroundColor: '#d1fae5', borderRadius: '6px', borderLeft: '4px solid #3EA43B' }}>
                  <span className="approval-label" style={{ fontWeight: '600', color: '#02236A' }}>Approved on: </span>
                  <span className="approval-text" style={{ color: '#3EA43B' }}>{request.verifiedAt}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Document Viewer Modal */}
      {selectedRequest && createPortal(
        <div className="document-modal-overlay" onClick={handleCloseDetails}>
          <div className="document-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>KYB Documents - {selectedRequest.name}</h2>
              <button className="close-btn" onClick={handleCloseDetails}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-details-section">
                <h3>User Information</h3>
                <div className="user-info-grid">
                  <div className="info-item">
                    <span className="info-label">Full Name:</span>
                    <span className="info-value">{selectedRequest.name}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{selectedRequest.email}</span>
                  </div>
                  {selectedRequest.phone && selectedRequest.phone !== 'N/A' && (
                    <div className="info-item">
                      <span className="info-label">Phone:</span>
                      <span className="info-value">{selectedRequest.phone}</span>
                    </div>
                  )}
                  {selectedRequest.address && selectedRequest.address !== 'N/A' && (
                    <div className="info-item">
                      <span className="info-label">Address:</span>
                      <span className="info-value">{selectedRequest.address}</span>
                    </div>
                  )}
                  {selectedRequest.dateOfBirth && selectedRequest.dateOfBirth !== 'N/A' && (
                    <div className="info-item">
                      <span className="info-label">Date of Birth:</span>
                      <span className="info-value">{selectedRequest.dateOfBirth}</span>
                    </div>
                  )}
                  {selectedRequest.idType && selectedRequest.idType !== 'N/A' && (
                    <div className="info-item">
                      <span className="info-label">ID Type:</span>
                      <span className="info-value">{selectedRequest.idType}</span>
                    </div>
                  )}
                  {selectedRequest.idNumber && selectedRequest.idNumber !== 'N/A' && (
                    <div className="info-item">
                      <span className="info-label">ID Number:</span>
                      <span className="info-value">{selectedRequest.idNumber}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <span className="info-label">Role:</span>
                    <span className="info-value">{selectedRequest.role}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Submitted Date:</span>
                    <span className="info-value">{selectedRequest.submittedDate}</span>
                  </div>
                </div>
              </div>

              <div className="documents-section">
                <h3>Business Documents</h3>
                <div className="documents-grid">
                  {selectedRequest.propertyOwnershipDocument && (
                    <div className="document-item">
                      <div className="document-preview">
                        {imageLoading.document1 ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#8B8B8B' }}>
                            Loading...
                          </div>
                        ) : imageUrls.document1 ? (
                          isPDF(selectedRequest.propertyOwnershipDocument) ? (
                            <iframe
                              src={imageUrls.document1}
                              title="Document 1"
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                border: 'none', 
                                borderRadius: '8px',
                                display: 'block'
                              }}
                              onLoad={() => {
                                setImageLoading(prev => ({ ...prev, document1: false }));
                              }}
                              onError={() => {
                                console.error('Failed to load PDF');
                                setImageLoading(prev => ({ ...prev, document1: false }));
                              }}
                            />
                          ) : (
                            <img 
                              src={imageUrls.document1} 
                              alt="Document 1" 
                              style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                              onLoad={() => {
                                setImageLoading(prev => ({ ...prev, document1: false }));
                              }}
                              onError={(e) => {
                                setImageLoading(prev => ({ ...prev, document1: false }));
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<span class="document-placeholder">🏠</span><p class="document-name">Failed to load image</p>';
                              }}
                            />
                          )
                        ) : (
                          <>
                            <span className="document-placeholder">🏠</span>
                            <p className="document-name">{selectedRequest.propertyOwnershipDocument}</p>
                          </>
                        )}
                      </div>
                      <p className="document-label">Document 1</p>
                      {selectedRequest.propertyOwnershipDocument.startsWith('http') && imageUrls.document1 && (
                        <button 
                          onClick={() => handleOpenInNewTab(imageUrls.document1, selectedRequest.propertyOwnershipDocument)}
                          style={{ 
                            fontSize: '12px', 
                            color: '#02236A', 
                            textDecoration: 'underline', 
                            marginTop: '4px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            fontFamily: 'inherit'
                          }}
                        >
                          Open in new tab
                        </button>
                      )}
                    </div>
                  )}
                  {selectedRequest.kraPinCertificate && (
                    <div className="document-item">
                      <div className="document-preview">
                        {imageLoading.document2 ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#8B8B8B' }}>
                            Loading...
                          </div>
                        ) : imageUrls.document2 ? (
                          isPDF(selectedRequest.kraPinCertificate) ? (
                            <iframe
                              src={imageUrls.document2}
                              title="Document 2"
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                border: 'none', 
                                borderRadius: '8px',
                                display: 'block'
                              }}
                              onLoad={() => {
                                setImageLoading(prev => ({ ...prev, document2: false }));
                              }}
                              onError={() => {
                                console.error('Failed to load PDF');
                                setImageLoading(prev => ({ ...prev, document2: false }));
                              }}
                            />
                          ) : (
                            <img 
                              src={imageUrls.document2} 
                              alt="Document 2" 
                              style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                              onLoad={() => {
                                setImageLoading(prev => ({ ...prev, document2: false }));
                              }}
                              onError={(e) => {
                                setImageLoading(prev => ({ ...prev, document2: false }));
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<span class="document-placeholder">📋</span><p class="document-name">Failed to load image</p>';
                              }}
                            />
                          )
                        ) : (
                          <>
                            <span className="document-placeholder">📋</span>
                            <p className="document-name">{selectedRequest.kraPinCertificate}</p>
                          </>
                        )}
                      </div>
                      <p className="document-label">Document 2</p>
                      {selectedRequest.kraPinCertificate.startsWith('http') && imageUrls.document2 && (
                        <button 
                          onClick={() => handleOpenInNewTab(imageUrls.document2, selectedRequest.kraPinCertificate)}
                          style={{ 
                            fontSize: '12px', 
                            color: '#02236A', 
                            textDecoration: 'underline', 
                            marginTop: '4px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            fontFamily: 'inherit'
                          }}
                        >
                          Open in new tab
                        </button>
                      )}
                    </div>
                  )}
                  {selectedRequest.businessRegistrationDocument && (
                    <div className="document-item">
                      <div className="document-preview">
                        {imageLoading.document3 ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#8B8B8B' }}>
                            Loading...
                          </div>
                        ) : imageUrls.document3 ? (
                          isPDF(selectedRequest.businessRegistrationDocument) ? (
                            <iframe
                              src={imageUrls.document3}
                              title="Document 3"
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                border: 'none', 
                                borderRadius: '8px',
                                display: 'block'
                              }}
                              onLoad={() => {
                                setImageLoading(prev => ({ ...prev, document3: false }));
                              }}
                              onError={() => {
                                console.error('Failed to load PDF');
                                setImageLoading(prev => ({ ...prev, document3: false }));
                              }}
                            />
                          ) : (
                            <img 
                              src={imageUrls.document3} 
                              alt="Document 3" 
                              style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                              onLoad={() => {
                                setImageLoading(prev => ({ ...prev, document3: false }));
                              }}
                              onError={(e) => {
                                setImageLoading(prev => ({ ...prev, document3: false }));
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<span class="document-placeholder">📄</span><p class="document-name">Failed to load image</p>';
                              }}
                            />
                          )
                        ) : (
                          <>
                            <span className="document-placeholder">📄</span>
                            <p className="document-name">{selectedRequest.businessRegistrationDocument}</p>
                          </>
                        )}
                      </div>
                      <p className="document-label">Document 3</p>
                      {selectedRequest.businessRegistrationDocument.startsWith('http') && imageUrls.document3 && (
                        <button 
                          onClick={() => handleOpenInNewTab(imageUrls.document3, selectedRequest.businessRegistrationDocument)}
                          style={{ 
                            fontSize: '12px', 
                            color: '#02236A', 
                            textDecoration: 'underline', 
                            marginTop: '4px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            fontFamily: 'inherit'
                          }}
                        >
                          Open in new tab
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {selectedRequest.status === 'pending' && (
                <div className="modal-actions">
                  <button 
                    className="btn btn-verify-modal" 
                    onClick={() => {
                      handleVerify(selectedRequest.id);
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Approve KYB'}
                  </button>
                  <button 
                    className="btn btn-reject-modal" 
                    onClick={() => {
                      handleReject(selectedRequest.id);
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Reject KYB'}
                  </button>
                </div>
              )}
              
              {selectedRequest.status === 'rejected' && selectedRequest.rejectionReason && (
                <div className="rejection-reason" style={{ marginTop: '24px' }}>
                  <span className="rejection-label">Rejection Reason:</span>
                  <span className="rejection-text">{selectedRequest.rejectionReason}</span>
                  {selectedRequest.rejectionReasonCode && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#8B8B8B' }}>
                      Code: {selectedRequest.rejectionReasonCode}
                    </div>
                  )}
                </div>
              )}
              
              {selectedRequest.status === 'verified' && selectedRequest.verifiedAt && (
                <div className="approval-info" style={{ marginTop: '24px', padding: '12px', backgroundColor: '#d1fae5', borderRadius: '6px', borderLeft: '4px solid #3EA43B' }}>
                  <span className="approval-label">Approved on:</span>
                  <span className="approval-text">{selectedRequest.verifiedAt}</span>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Error Modal */}
      {errorModalOpen && createPortal(
        <div className="document-modal-overlay" onClick={() => setErrorModalOpen(false)}>
          <div className="document-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ color: '#EF4444' }}>Error</h2>
              <button className="close-btn" onClick={() => setErrorModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ padding: '20px 0' }}>
                <p style={{ 
                  fontSize: '16px', 
                  color: '#02236A',
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  {errorModalMessage}
                </p>
              </div>
              <div className="modal-actions">
                <button 
                  className="btn btn-view" 
                  onClick={() => setErrorModalOpen(false)}
                  style={{ flex: 1 }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Success Modal */}
      {successModalOpen && createPortal(
        <div key="success-modal" className="document-modal-overlay" onClick={() => setSuccessModalOpen(false)}>
          <div className="document-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ color: '#3EA43B' }}>Success</h2>
              <button className="close-btn" onClick={() => setSuccessModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ padding: '20px 0' }}>
                <p style={{ 
                  fontSize: '16px', 
                  color: '#02236A',
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  {successModalMessage}
                </p>
              </div>
              <div className="modal-actions">
                <button 
                  className="btn btn-verify-modal" 
                  onClick={() => setSuccessModalOpen(false)}
                  style={{ flex: 1 }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Rejection Modal */}
      {rejectModalOpen && createPortal(
        <div className="document-modal-overlay" onClick={handleCloseRejectModal}>
          <div className="document-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Reject KYB Verification</h2>
              <button className="close-btn" onClick={handleCloseRejectModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="rejection-form">
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label htmlFor="kyb-rejection-reason" style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#02236A'
                  }}>
                    Rejection Reason <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <textarea
                    id="kyb-rejection-reason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter the reason for rejection..."
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '12px',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '14px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label htmlFor="kyb-rejection-reason-code" style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#02236A'
                  }}>
                    Rejection Reason Code <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <select
                    id="kyb-rejection-reason-code"
                    value={rejectionReasonCode}
                    onChange={(e) => setRejectionReasonCode(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '14px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2302236A' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '12px',
                      paddingRight: '40px'
                    }}
                    disabled={isLoading}
                  >
                    <option value="">Select a reason code</option>
                    <option value="BLURRY_DOCUMENT">Blurry Document</option>
                    <option value="EXPIRED_DOCUMENT">Expired Document</option>
                    <option value="INVALID_DOCUMENT">Invalid Document</option>
                    <option value="MISMATCHED_INFORMATION">Mismatched Information</option>
                    <option value="INCOMPLETE_DOCUMENT">Incomplete Document</option>
                    <option value="POOR_QUALITY">Poor Quality</option>
                    <option value="SUSPICIOUS_DOCUMENT">Suspicious Document</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div className="modal-actions">
                  <button 
                    className="btn btn-view" 
                    onClick={handleCloseRejectModal}
                    disabled={isLoading}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-reject-modal" 
                    onClick={handleSubmitRejection}
                    disabled={isLoading || !rejectionReason.trim() || !rejectionReasonCode.trim()}
                    style={{ flex: 1 }}
                  >
                    {isLoading ? 'Processing...' : 'Reject KYB'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Pagination - Show if there are multiple pages or if not on the last page */}
      {(totalPages > 1 || !isLastPage || page > 0) && (
        <div className="pagination" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '12px', 
          marginTop: '32px',
          padding: '16px'
        }}>
          <button 
            onClick={() => setPage(prev => Math.max(0, prev - 1))}
            disabled={isFirstPage || isLoading}
            style={{
              padding: '8px 16px',
              backgroundColor: isFirstPage ? '#e0e0e0' : '#02236A',
              color: isFirstPage ? '#8B8B8B' : '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: isFirstPage ? 'not-allowed' : 'pointer',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '14px'
            }}
          >
            Previous
          </button>
          <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#02236A' }}>
            Page {page + 1} {totalPages > 0 ? `of ${totalPages}` : ''} {totalElements > 0 ? `(${totalElements} total)` : ''}
          </span>
          <button 
            onClick={() => setPage(prev => prev + 1)}
            disabled={isLastPage || isLoading}
            style={{
              padding: '8px 16px',
              backgroundColor: isLastPage ? '#e0e0e0' : '#02236A',
              color: isLastPage ? '#8B8B8B' : '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: isLastPage ? 'not-allowed' : 'pointer',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '14px'
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default KYBManagement;
